/**
 * Shared modal documentation blocks. Used by modal components to assemble their
 * metadata description. Keys: namedBlockCta, contextualActions, onBeforeClose, footerCta, testing
 */
export const MODAL_DOCS_BLOCKS = {
  namedBlockCta: `
### Named blocks
This modal exposes the \`:cta\` named block, which allows customization of the footer buttons.
If this named block is not used, the \`@primaryBtnAction\` and \`@primaryBtnText\` arguments are required.
`,
  contextualActions: `
### Contextual actions
Two actions are yielded by the component: \`beforeClose\` and \`close\`. A customized footer button, or any
other interactive component, uses them to dismiss the modal.

\`beforeClose\` is the guarded entry point — it consults \`onBeforeClose\` and dismisses only if that hook
allows it. \`close\` dismisses outright without asking. Neither triggers \`onClose\`, which fires on its own
once the dismissal has finished.
`,
  onBeforeClose: `
### onBeforeClose
This action provides a way to interrupt the closing process of the modal. It must return
a Boolean value. If the returned value is \`true\`, then the modal will continue with the
closing process. If the value is \`false\`, then the closing process will be aborted. This
action should be used to perform tasks like checking for unsaved changes in the model.
`,
  footerCta: `
### Built-in Footer CTA
When using the default footer CTA buttons, your provided action will receive the \`@onClose\`
action as their first argument. You can close the modal by running this action at the end of
your action's code.

Both \`@secondaryBtnAction\` and \`@secondaryBtnText\` must be provided for the secondary button
to be added to the footer.

\`@primaryBtnAction\` and \`@primaryBtnText\` are required when using the default CTA buttons.
`,
  testing: `
### Testing
While our legacy modals require the use of specialized waiters to render for snapshots, the new
DS modals do not require any special handling.
`,
};

/** Modals with default footer CTA (acknowledgement, transactional, destructive, announcement). */
export const MODAL_DOCS_CTA_BLOCKS = [
  'namedBlockCta',
  'contextualActions',
  'onBeforeClose',
  'footerCta',
  'testing',
];

/** Standard modal (no built-in footer CTA). */
export const MODAL_DOCS_STANDARD_BLOCKS = ['contextualActions', 'onBeforeClose', 'testing'];

/** Full-screen modal. */
export const MODAL_DOCS_FULL_SCREEN_BLOCKS = ['onBeforeClose', 'testing'];

/** Flyout. */
export const MODAL_DOCS_FLYOUT_BLOCKS = ['testing'];

/**
 * Assembles a modal's full description from unique copy and shared block keys.
 * @param {string} uniqueCopy - Component-specific description text
 * @param {string[]} blockKeys - Keys from MODAL_DOCS_BLOCKS to append (e.g. ['namedBlockCta', 'contextualActions', 'onBeforeClose', 'footerCta', 'testing'])
 * @returns {string}
 */
export function assembleModalDescription(uniqueCopy, blockKeys) {
  const blocks = (blockKeys ?? []).map(key => MODAL_DOCS_BLOCKS[key]).filter(Boolean);
  const parts = [uniqueCopy, ...blocks].filter(Boolean);
  return parts.join('\n');
}
